import React, { useEffect, useRef, useState } from 'react';
import DateUtils from '@zvuk-b2b/react-uikit/toolkit/DateUtils';
import { MediafilePoster } from '@entities/media';
import type { MediafilePosterProps, MediafilePosterTone } from '@entities/media';
import { MediafilePreview } from 'application/components/MediafilePreview';
import { DEFAULT_PREVIEW_DURATION } from 'application/components/VideoMediafileItemPoster/consts';
import { getCoverKey } from 'application/components/utils';
import { FALLBACK_TONE_COUNT } from './consts';
import { TEXTS } from './texts';
import type { MediafilePosterPreviewProps, OpenPreviewResource } from './types';

const MediafilePosterPreview: React.FC<MediafilePosterPreviewProps> = ({
  id,
  kind,
  title,
  thumbnail,
  duration,
  extension,
  url,
  isVideo,
}) => {
  const [openResource, setOpenResource] = useState<OpenPreviewResource>();
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const canPreview = Boolean(url) && typeof isVideo === 'boolean';
  const isOpen = canPreview && openResource?.url === url && openResource?.isVideo === isVideo;
  const imageUrl = kind === 'image' && isVideo === false ? url : undefined;
  const thumbnailUrl = thumbnail || imageUrl;

  useEffect(() => setOpenResource(undefined), [url, isVideo, canPreview]);

  const handleClose = () => {
    setOpenResource(undefined);
    if (triggerRef.current?.isConnected) triggerRef.current.focus({ preventScroll: true });
  };

  const posterProps: MediafilePosterProps = {
    ...(kind === 'video' ? { kind: 'video' } : { kind: 'image' }),
    id,
    state: 'ready',
    thumbnail: thumbnailUrl ? { src: thumbnailUrl, fallbackSrc: imageUrl, alt: '' } : undefined,
    fallbackTone: getCoverKey(title, FALLBACK_TONE_COUNT) as MediafilePosterTone,
    formatLabel: extension ?? undefined,
    durationLabel: DateUtils.msToHMS(duration || DEFAULT_PREVIEW_DURATION, ['hours', 'minutes', 'seconds']),
    preview: canPreview
      ? {
          label: `${TEXTS.PREVIEW}: ${title}`,
          onRequest: (event: React.MouseEvent<HTMLButtonElement>) => {
            if (!url || typeof isVideo !== 'boolean') return;
            triggerRef.current = event.currentTarget;
            setOpenResource({ url, isVideo });
          },
        }
      : undefined,
  };

  return (
    <>
      {/* Preserve the poster union while forwarding only the explicitly constructed UI props. */}
      {/* eslint-disable-next-line react/jsx-props-no-spreading */}
      <MediafilePoster {...posterProps} />
      {isOpen && openResource && (
        <MediafilePreview
          url={openResource.url}
          isVideo={openResource.isVideo}
          onClose={handleClose}
        />
      )}
    </>
  );
};

export default MediafilePosterPreview;
